/**
 * Input mask and validation utilities for Brazilian documents.
 * Used client-side for instant feedback; server also validates.
 */

// ─── CNPJ ────────────────────────────────────────────

/**
 * Formats a fully-typed 14-digit CNPJ to XX.XXX.XXX/XXXX-XX.
 * Returns partial string as-is if fewer than 14 digits.
 */
export function formatCnpj(v = '') {
  const d = String(v).replace(/\D/g, '').slice(0, 14);
  if (d.length < 14) return d;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12)}`;
}

/**
 * Applies CNPJ mask: XX.XXX.XXX/XXXX-XX
 * Strips non-digits first, then applies progressive masking.
 */
export function maskCnpj(value) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

/**
 * Strips all non-digits from a CNPJ string.
 */
export function stripCnpj(cnpj) {
  return (cnpj || '').replace(/\D/g, '');
}

/**
 * Validates CNPJ using the official Brazilian check-digit algorithm.
 * Returns { valid, error? }
 */
export function validateCnpj(cnpj) {
  const digits = stripCnpj(cnpj);

  if (digits.length !== 14) {
    return { valid: false, error: 'CNPJ deve ter 14 dígitos' };
  }

  // Reject all-same-digit CNPJs
  if (/^(\d)\1{13}$/.test(digits)) {
    return { valid: false, error: 'CNPJ inválido' };
  }

  // First check digit
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(digits[i]) * weights1[i];
  }
  let remainder = sum % 11;
  const check1 = remainder < 2 ? 0 : 11 - remainder;
  if (parseInt(digits[12]) !== check1) {
    return { valid: false, error: 'CNPJ inválido (dígito verificador incorreto)' };
  }

  // Second check digit
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(digits[i]) * weights2[i];
  }
  remainder = sum % 11;
  const check2 = remainder < 2 ? 0 : 11 - remainder;
  if (parseInt(digits[13]) !== check2) {
    return { valid: false, error: 'CNPJ inválido (dígito verificador incorreto)' };
  }

  return { valid: true };
}

// ─── CEP ─────────────────────────────────────────────

/**
 * Formats a fully-typed 8-digit CEP to XXXXX-XXX.
 * Returns partial string as-is if fewer than 8 digits.
 */
export function formatCep(v = '') {
  const d = String(v).replace(/\D/g, '').slice(0, 8);
  if (d.length < 6) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

/**
 * Applies CEP mask: XXXXX-XXX
 */
export function maskCep(value) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 8);
  return digits.replace(/^(\d{5})(\d)/, '$1-$2');
}

/**
 * Strips all non-digits from a CEP string.
 */
export function stripCep(cep) {
  return (cep || '').replace(/\D/g, '');
}

/**
 * Validates CEP format: exactly 8 digits, not all zeros.
 * Returns { valid, error? }
 */
export function validateCep(cep) {
  const digits = stripCep(cep);

  if (digits.length !== 8) {
    return { valid: false, error: 'CEP deve ter 8 dígitos' };
  }

  if (/^0{8}$/.test(digits)) {
    return { valid: false, error: 'CEP inválido' };
  }

  return { valid: true };
}

/**
 * Looks up a CEP using the ViaCEP API.
 * Returns address data or null if not found.
 */
export async function lookupCep(cep) {
  const digits = stripCep(cep);
  if (digits.length !== 8) return null;

  try {
    const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    const data = await response.json();
    if (data.erro) return null;
    return {
      endereco_rua: data.logradouro || '',
      endereco_bairro: data.bairro || '',
      cidade: data.localidade || '',
      estado: data.uf || '',
    };
  } catch {
    return null;
  }
}
